import type {
  ConversationMessageRow,
  ConversationRun
} from '@axilon-apps/domains-ontology-ui-state'

import { flattenMessagesToToolCalls } from '../ToolTrace/MessageList'
import type { ToolCallRowEntry } from '../ToolTrace/ToolCallRow'

/**
 * Every tool call a persisted conversation made, in order, as rows for
 * `SidebarToolCallList`. Latency comes from the message clock: a tool_use is
 * persisted with its assistant message and its tool_result with the next
 * user message, so the gap between those two rows is the call's wall time
 * (a batch of parallel calls shares one gap). `null` when either row is
 * missing or the clocks disagree.
 */
export function conversationToolCallEntries(turns: readonly ConversationRun[]): ToolCallRowEntry[] {
  const out: ToolCallRowEntry[] = []
  for (const run of turns) {
    const timing = toolCallTimingFromMessages(run.messages)
    for (const call of flattenMessagesToToolCalls(run.messages)) {
      const at = timing.get(call.id)
      out.push({
        id: call.id,
        name: call.name,
        input: call.input,
        output: call.output,
        parentStepIndex: 0,
        agentKind: run.agent_kind,
        depth: 0,
        status: call.status,
        batchId: `${run.id}:${call.batchId}`,
        durationMs: at?.durationMs ?? null,
        ...(at?.startedAt !== undefined ? { startedAt: at.startedAt } : {})
      })
    }
  }
  return out
}

interface ToolCallTiming {
  startedAt?: number
  durationMs: number | null
}

function toolCallTimingFromMessages(
  messages: readonly ConversationMessageRow[]
): Map<string, ToolCallTiming> {
  const startedById = new Map<string, number>()
  const finishedById = new Map<string, number>()
  for (const message of messages) {
    const at = Date.parse(message.created_at)
    if (!Number.isFinite(at) || !Array.isArray(message.content)) continue
    for (const block of message.content as unknown[]) {
      if (!block || typeof block !== 'object') continue
      const rec = block as Record<string, unknown>
      if (message.role === 'assistant' && rec.type === 'tool_use' && typeof rec.id === 'string') {
        startedById.set(rec.id, at)
      } else if (message.role === 'user') {
        const id = toolResultId(rec)
        if (id) finishedById.set(id, at)
      }
    }
  }
  const out = new Map<string, ToolCallTiming>()
  for (const [id, startedAt] of startedById) {
    const finishedAt = finishedById.get(id)
    const durationMs =
      finishedAt !== undefined && finishedAt >= startedAt ? finishedAt - startedAt : null
    out.set(id, { startedAt, durationMs })
  }
  return out
}

/** Anthropic (`tool_use_id`) and Bedrock (`toolResult.toolUseId`) result shapes. */
function toolResultId(block: Record<string, unknown>): string | null {
  if (block.type === 'tool_result' && typeof block.tool_use_id === 'string')
    return block.tool_use_id
  const bedrock = block.toolResult
  if (bedrock && typeof bedrock === 'object') {
    const id = (bedrock as { toolUseId?: unknown }).toolUseId
    if (typeof id === 'string') return id
  }
  return null
}

/** Wall time from the first run's start to the last run's end (or now while one is open). */
export function conversationElapsedMs(
  turns: readonly ConversationRun[],
  now = Date.now()
): number | null {
  let start = Number.POSITIVE_INFINITY
  let end = Number.NEGATIVE_INFINITY
  let open = false
  for (const run of turns) {
    const startedAt = Date.parse(run.started_at)
    if (Number.isFinite(startedAt)) start = Math.min(start, startedAt)
    if (run.status === 'running' || !run.finished_at) {
      open = true
      continue
    }
    const finishedAt = Date.parse(run.finished_at)
    if (Number.isFinite(finishedAt)) end = Math.max(end, finishedAt)
  }
  if (!Number.isFinite(start)) return null
  const stop = open ? Math.max(end, now) : end
  return Number.isFinite(stop) && stop >= start ? stop - start : null
}
